import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCurrentPage, setDownloadAppBar } from "../../feature/userData/userData";
import submitAgentLinkLead from "../../service/api/user/submitAgentLinkLead";
import FunnelVideo from "./FunnelVideo";
import NotYetLeadForm from "./NotYetLeadForm";
import {
  PROJECT_TYPE_KEYS,
  PROJECT_TYPES,
  TIMING_LATER,
  TIMING_NOW,
} from "./funnelEasyEstimate";
import "./addLink.scss";

const STEP_PROJECT = 1;
const STEP_TIMING = 2;

const FUNNEL_STEPS = [
  { id: STEP_PROJECT, label: "Project" },
  { id: STEP_TIMING, label: "When" },
];

function FunnelProgress({ step }) {
  return (
    <nav className="add-link-funnel-progress" aria-label="Funnel progress">
      {FUNNEL_STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div
            className={`add-link-funnel-progress__item${
              step >= s.id ? " is-done" : ""
            }${step === s.id ? " is-current" : ""}`}
          >
            <span className="add-link-funnel-progress__dot">{i + 1}</span>
            <span className="add-link-funnel-progress__label">{s.label}</span>
          </div>
          {i < FUNNEL_STEPS.length - 1 && (
            <span
              className={`add-link-funnel-progress__line${
                step > s.id ? " is-done" : ""
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function FunnelStepLayout({
  showBack,
  onBack,
  header,
  pillsLayout = "stack",
  children,
}) {
  return (
    <div className="add-link-funnel-step">
      {showBack && (
        <div className="add-link-funnel-toolbar">
          <button
            type="button"
            className="add-link-funnel-back"
            onClick={onBack}
            aria-label="Go back"
          >
            ← Back
          </button>
        </div>
      )}
      <div className="add-link-funnel-body">
        <div className="add-link-funnel-media">
          <h1 className="add-link-funnel-header">{header}</h1>
          <div className="add-link-funnel-video">
            <FunnelVideo />
          </div>
        </div>
        <div className="add-link-funnel-actions">
          <div className={`add-link-funnel-pills add-link-funnel-pills--${pillsLayout}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

const AddLinkNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP_PROJECT);
  const [projectKey, setProjectKey] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);

  const project = projectKey ? PROJECT_TYPES[projectKey] : null;

  useEffect(() => {
    dispatch(setCurrentPage("Easy Estimate"));
    dispatch(setDownloadAppBar(false));
  }, [dispatch]);

  const goToStep = (nextStep) => {
    setAnimating(true);
    setTimeout(() => {
      setStep(nextStep);
      setAnimating(false);
    }, 180);
  };

  const openLeadForm = () => {
    if (step !== STEP_TIMING) return;
    setLeadFormOpen(true);
  };

  const closeLeadForm = () => setLeadFormOpen(false);

  const selectProject = (key) => {
    setProjectKey(key);
    goToStep(STEP_TIMING);
  };

  const goBack = () => {
    setLeadFormOpen(false);
    if (step === STEP_TIMING) {
      setProjectKey(null);
      goToStep(STEP_PROJECT);
    }
  };

  const buildAnswers = () => {
    if (!project) return [];
    return [
      {
        id: "project_type",
        prompt: "What Type of Project Do You Need Help With?",
        answer: `${project.emoji} ${project.label}`,
      },
      {
        id: "timing",
        prompt: project.timingHeader,
        answer: TIMING_NOW,
      },
    ];
  };

  const handleLeadSubmit = (fields) => {
    const problemAnswer = project
      ? `${project.emoji} ${project.label} — ${TIMING_LATER}`
      : TIMING_LATER;
    return new Promise((resolve, reject) => {
      dispatch(
        submitAgentLinkLead(
          {
            name: fields.name,
            email: fields.email,
            phone: fields.phone,
            agent_link_problem_chr: problemAnswer,
            agent_link_source_chr: "easy_estimate_link",
          },
          () => resolve(),
          () => reject(new Error("submit failed"))
        )
      );
    });
  };

  const sendToScanNew = () => {
    navigate("/scanNew", {
      state: {
        agentLinkAnswers: buildAnswers(),
        agentLinkCompletedAt: new Date().toISOString(),
      },
    });
  };

  const selectTiming = (choice) => {
    if (choice === TIMING_NOW) {
      sendToScanNew();
      return;
    }
    openLeadForm();
  };

  let stepContent = null;

  if (step === STEP_PROJECT) {
    stepContent = (
      <FunnelStepLayout
        header="What Type of Project Do You Need Help With?"
        pillsLayout="cta"
      >
        {PROJECT_TYPE_KEYS.map((key) => {
          const p = PROJECT_TYPES[key];
          return (
            <button
              key={key}
              type="button"
              className="add-link-pill-btn"
              onClick={() => selectProject(key)}
            >
              {p.emoji}&nbsp;&nbsp;{p.label}
            </button>
          );
        })}
      </FunnelStepLayout>
    );
  } else if (step === STEP_TIMING && project) {
    stepContent = (
      <FunnelStepLayout
        showBack
        onBack={goBack}
        header={project.timingHeader}
        pillsLayout="cta"
      >
        <button
          type="button"
          className="add-link-pill-btn add-link-pill-btn--success"
          onClick={() => selectTiming(TIMING_NOW)}
        >
          🚀&nbsp;&nbsp;{TIMING_NOW}
        </button>
        <button
          type="button"
          className="add-link-pill-btn add-link-pill-btn--secondary"
          onClick={() => selectTiming(TIMING_LATER)}
        >
          📅&nbsp;&nbsp;{TIMING_LATER}
        </button>
      </FunnelStepLayout>
    );
  }

  return (
    <div className="add-link-funnel-page">
      <div className="add-link-funnel-overlay">
        {leadFormOpen && (
          <NotYetLeadForm
            title="We'll send you a reminder in 30 days."
            prompt="What's your email or phone?"
            submitLabel="Submit"
            onClose={closeLeadForm}
            onSubmit={handleLeadSubmit}
            onDone={() => {
              closeLeadForm();
              navigate("/");
            }}
          />
        )}
        <div className="add-link-funnel-shell">
          <header className="add-link-funnel-top">
            <div className="add-link-funnel-brand">
              <span className="add-link-funnel-brand-mark">Chilll</span>
              <span className="add-link-funnel-brand-sub">Easy Estimate</span>
            </div>
            <FunnelProgress step={step} />
          </header>
          <div
            className={`add-link-funnel-card${
              animating ? " add-link-funnel-card--anim" : ""
            }`}
            key={step}
          >
            <div className="add-link-funnel-handle" aria-hidden />
            {stepContent}
            <a
              href="https://chilll.com"
              target="_blank"
              rel="noopener noreferrer"
              className="add-link-funnel-logo"
            >
              CHILLL.COM
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLinkNew;
